import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import baseURL from "../component/baseURL";
import { setAppLanguageAndNotify } from "../utils/appLanguage";
import MainSidebar from "../pages/MainSidebar";
import MyConnection from "./MyConnection";
import ConnectionRequest from "./ConnectionRequest";
import GlobalMembers from "./GlobalMembers";
import MemberFilter from "./MemberFilter";
import MemberFilterMyConection from "./MemberFilterMyConection";
import MyConnectionCreateFilter from "./MyConnectionCreateFilter";
import MemberCreateFilter from "./MemberCreateFilter";
import BlockedMembers from "./BlockedMembers";
import MyListings from "./MyListings";
import AddListing from "./AddListing";
import InactiveListing from "./InactiveListing";
import MyListingContract from "./MyListingContract";
import EditListing from "./EditListing";
import GlobalPortfolio from "./GlobalPortfolio";
import PortfolioFavorite from "./PortfolioFavorite";
import PortfolioFilter from "./PortfolioFilter";
import MyPortfolio from "./MyPortfolio";
import ConnectionRequestSend from "./ConnectionRequestSend";
import MembersProfile from "./MembersProfile";
import PortfolioCreateFilter from "./PortfolioCreateFilter";
/** Treat /home and /home/ the same for hub vs sub-routes */
function normalizePath(pathname) {
    return (pathname || '/').replace(/\/$/, '') || '/';
}

/** Main list vs viewing another member's data (connections, listings, etc.) */
function isHomeMemberSubView(loc) {
    if (loc.state?.fromSidebar === true) {
        return false;
    }
    const stateIsMember = loc.state?.is_member;
    if (stateIsMember === 1 || stateIsMember === '1') {
        return true;
    }
    const searchParams = new URLSearchParams(loc.search || '');
    if (searchParams.get('is_member') === '1') {
        return true;
    }
    try {
        return localStorage.getItem('is_member') === '1';
    } catch {
        return false;
    }
}

export default function Home() {
    const location = useLocation();
    const navigate = useNavigate();
    const homeRouteStackRef = useRef([]);
    const suppressStackPushRef = useRef(false);
    const [activeItem, setActiveItem] = useState("My Connection");
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isPortfolioDropdownOpen, setIsPortfolioDropdownOpen] = useState(false);
    const portfolioDropdownRef = useRef(null);
    const portfolioButtonRef = useRef(null);
    const [userID, setUserID] = useState(null);
    const [availableListingCount, setAvailableListingCount] = useState(0);
    // Map URL paths to item names (for mobile routing)
    const pathToItemMap = {
        '/home': 'My Connection',
        '/home/my-connection': 'My Connection',
        '/home/connection-request': 'Connection Request',
        '/home/connection-request-send': 'Connection Request Send',
        '/home/global-members': 'Global Members',
        '/home/global-members-details': 'Global Members',
        '/home/member-filter': 'Member Filter',
        '/home/member-filter-my-connection': 'My Connection',
        '/home/my-connection-create-filter': 'My Connection',
        '/home/member-create-filter': 'Member Filter',
        '/home/blocked-members': 'Blocked Members',
        '/home/my-listings': 'My Listings',
        '/home/add-listing': 'My Listings',
        '/home/edit-listing': 'My Listings',
        '/home/inactive-listing': 'My Listings',
        '/home/my-listing-contract': 'My Listings',
        '/home/my-portfolio': 'My Portfolio',
        '/home/global-portfolio': 'Global Portfolio'
        , '/home/portfolio-filter': 'Global Portfolio'
        , '/home/portfolio-favorite': 'Global Portfolio'
        , '/home/portfolio-create-filter': 'Global Portfolio'
    };

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        setUserID(userid);
        // const authKey = localStorage.getItem('auth_key');
        // setAuthKey(authKey);
        fetchDetailsUser(userid);

        countListings(userid);
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Sync activeItem based on device type and (on member-profile) the page we came from
    useEffect(() => {
        const fromPath = location.state?.from;
        const isMemberProfile = location.pathname === '/member-profile' || location.pathname === '/home/global-members-details';

        if (isMemberProfile && fromPath) {
            // On MembersProfile: highlight the nav item of the page we came from
            const itemFromPath =
                fromPath === '/home/my-connection'
                    ? 'My Connection'
                    : fromPath === '/home/connection-request'
                        ? 'Connection Request'
                        : fromPath === '/home/global-members'
                            ? 'Global Members'
                            : 'Global Members';
            setActiveItem(itemFromPath);
            return;
        }

        if (location.pathname === '/home/blocked-members') {
            setActiveItem('Global Members');
            return;
        }
        if (isMobile) {
            if (normalizePath(location.pathname) === '/home') {
                setActiveItem('My Connection');
            } else if (isMemberProfile) {
                setActiveItem('Global Members');
            } else {
                const urlItem = pathToItemMap[location.pathname] || 'My Connection';
                setActiveItem(urlItem);
            }
        } else {
            // On desktop: sync activeItem from URL for all /home/* routes
            const urlItem = pathToItemMap[location.pathname];
            if (urlItem) {
                setActiveItem(urlItem);
            } else if (isMemberProfile) {
                setActiveItem('Global Members');
            } else if (normalizePath(location.pathname) === '/home' || location.pathname === '/home/my-connection') {
                setActiveItem('My Connection');
            }
        }
    }, [location.pathname, location.state?.from, isMobile]);

    // When navigating to global-members list, clear selected member so list shows (not profile)
    useEffect(() => {
        if (location.pathname === '/home/global-members') {
            setSelectedMemberId(null);
        }
    }, [location.pathname]);

    // Notify App component about MyConnection view (hide bottom nav on that screen — not on mobile /home hub where only sidebar shows)
    useEffect(() => {
        const onMobileHomeHub = isMobile && normalizePath(location.pathname) === '/home';
        const isViewingMyConnection = activeItem === "My Connection" && !onMobileHomeHub;
        window.dispatchEvent(new CustomEvent('myConnectionView', {
            detail: { isViewing: isViewingMyConnection }
        }));
    }, [activeItem, isMobile, location.pathname]);

    // Close portfolio dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                portfolioDropdownRef.current &&
                !portfolioDropdownRef.current.contains(event.target) &&
                portfolioButtonRef.current &&
                !portfolioButtonRef.current.contains(event.target)
            ) {
                setIsPortfolioDropdownOpen(false);
            }
        };

        if (isPortfolioDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isPortfolioDropdownOpen]);

    const togglePortfolioDropdown = () => {
        setIsPortfolioDropdownOpen(!isPortfolioDropdownOpen);
    };

    const itemToPathMap = {
        'My Connection': '/home/my-connection',
        'Connection Request': '/home/connection-request',
        'Connection Request Send': '/home/connection-request-send',
        'Global Members': '/home/global-members',
        'My Listings': '/home/my-listings',
        'My Portfolio': '/home/my-portfolio',
        'Global Portfolio': '/home/global-portfolio'
    };

    useEffect(() => {
        if (suppressStackPushRef.current) {
            suppressStackPushRef.current = false;
            return;
        }

        const currentEntry = {
            key: location.key,
            pathname: location.pathname,
            state: location.state || null
        };

        const nextStack = [...homeRouteStackRef.current];
        if (nextStack[nextStack.length - 1]?.key !== currentEntry.key) {
            nextStack.push(currentEntry);
        }

        // Keep stack bounded to avoid unbounded growth in long sessions.
        if (nextStack.length > 80) {
            nextStack.splice(0, nextStack.length - 80);
        }

        homeRouteStackRef.current = nextStack;
    }, [location.key, location.pathname, location.state]);

    const handleItemClick = (item) => {
        const path = itemToPathMap[item] || '/home/my-connection';
        if (item === "My Connection" || item === "My Listings" || item === "My Portfolio") {
            navigate(path, { state: { fromSidebar: true } });
        } else if (item === "Global Members") {
            navigate(path, { state: { fromGlobalMembersRoot: true } });
        } else if (item === "Global Portfolio") {
            navigate(path, { state: { fromGlobalPortfolioRoot: true } });
        } else {
            navigate(path);
        }
        setActiveItem(item);
        if (item === "Global Members") setSelectedMemberId(null);

        const isViewingMyConnection = item === "My Connection";
        window.dispatchEvent(new CustomEvent('myConnectionView', {
            detail: { isViewing: isViewingMyConnection }
        }));
    };

    // const handleBackClick = () => {
    //     const currentStack = [...homeRouteStackRef.current];
    //     const topEntry = currentStack[currentStack.length - 1];

    //     if (topEntry?.key === location.key) {
    //         currentStack.pop();
    //     }

    //     const previousEntry = currentStack[currentStack.length - 1];
    //     if (previousEntry) {
    //         homeRouteStackRef.current = currentStack;
    //         suppressStackPushRef.current = true;
    //         navigate(previousEntry.pathname, {
    //             replace: true,
    //             state: previousEntry.state
    //         });
    //         return;
    //     }

    //     if (window.history.length > 1) {
    //         navigate(-1);
    //         return;
    //     }

    //     navigate('/home');
    // };

    const handleBackClick = () => {
        const returnTo = location.state?.returnTo;
        if (returnTo?.path) {
            navigate(returnTo.path, { replace: true, state: returnTo.state ?? {} });
            return;
        }
        if (location.pathname === '/home/my-connection' && !isHomeMemberSubView(location)) {
            navigate('/home', { replace: true });
            return;
        }
        if (location.pathname === '/home/my-listings' && !isHomeMemberSubView(location)) {
            navigate('/home', { replace: true });
            return;
        }
        if (location.pathname === '/home/my-portfolio' && !isHomeMemberSubView(location)) {
            navigate('/home', { replace: true });
            return;
        }
        if (location.pathname === '/home/global-members' && !location.state?.returnTo) {
            navigate('/home', { replace: true });
            return;
        }
        if (location.pathname === '/home/global-portfolio' && !location.state?.returnTo) {
            navigate('/home', { replace: true });
            return;
        }
        navigate(-1);
    };

    // const handleBackClick = () => {
    //     if (location.pathname === '/member-profile' || location.pathname === '/home/global-members-details') {
    //         navigate('/home/global-members');
    //     } else if (location.pathname === '/home/member-filter') {
    //         navigate('/home/global-members');
    //     } else if (location.pathname === '/home/global-members') {
    //         navigate('/home');
    //     }
    //     else if (location.pathname === '/home/member-filter-my-connection') {
    //         navigate('/home/my-connection');
    //     } else if (location.pathname === '/home/my-connection-create-filter') {
    //         navigate('/home/member-filter-my-connection');
    //     } else if (location.pathname === '/home/portfolio-filter') {
    //         navigate('/home/global-portfolio');
    //     } else if (location.pathname === '/home/portfolio-favorite') {
    //         navigate('/home/global-portfolio');
    //     } else if (location.pathname === '/home/member-create-filter') {
    //         navigate('/home/member-filter');
    //     } else if (location.pathname === '/home/portfolio-create-filter') {
    //         navigate('/home/portfolio-filter');
    //     } else if (location.pathname === '/home/add-listing' || location.pathname === '/home/edit-listing' || location.pathname === '/home/inactive-listing' || location.pathname === '/home/my-listing-contract') {

    //         window.history.back();
    //         return;
    //     } else {
    //         navigate(-1);
    //     }
    //     window.dispatchEvent(new CustomEvent('myConnectionView', {
    //         detail: { isViewing: false }
    //     }));
    // };

    // Normalize so /home and /home/ both count as home hub (mobile: show main sidebar only, no content column).
    const isHomeHubPath = normalizePath(location.pathname) === '/home';
    const isMobileHomeHub = isMobile && isHomeHubPath;
    // Full-screen slide content (sidebar hidden on sub-routes)
    const isMobileContentPage = isMobile && !isHomeHubPath;
    const isMemberProfileRoute =
        location.pathname === '/member-profile' || location.pathname === '/home/global-members-details';
    /** MembersProfile renders its own mobile-page-header; avoid duplicate bar */
    const showDefaultMobilePageHeader = isMobileContentPage && !isMemberProfileRoute;

    const renderContent = () => {
        if (location.pathname === '/home/member-create-filter') {
            return <MemberCreateFilter />;
        }
        if (location.pathname === '/home/member-filter') {
            return <MemberFilter />;
        }
        if (location.pathname === '/home/member-filter-my-connection') {
            return <MemberFilterMyConection />;
        }
        if (location.pathname === '/home/my-connection-create-filter') {
            return <MyConnectionCreateFilter />;
        }
        if (location.pathname === '/home/portfolio-filter') {
            return <PortfolioFilter />;
        }
        if (location.pathname === '/home/portfolio-favorite') {
            return <PortfolioFavorite />;
        }
        if (location.pathname === '/home/portfolio-create-filter') {
            return <PortfolioCreateFilter />;
        }
        if (location.pathname === '/home/add-listing') {
            return <AddListing />;
        }
        if (location.pathname === '/home/edit-listing') {
            return <EditListing />;
        }
        if (location.pathname === '/home/inactive-listing') {
            return <InactiveListing />;
        }
        if (location.pathname === '/home/my-listing-contract') {
            return <MyListingContract />;
        }
        if (location.pathname === '/home/blocked-members') {
            return <BlockedMembers isMobile={isMobile} />;
        }
        switch (activeItem) {
            case "My Connection":
                return <MyConnection />;
            case "Connection Request":
                return (
                    <ConnectionRequest
                        isMobile={isMobile}
                        onOpenConnectionRequestSend={() => setActiveItem("Connection Request Send")}
                    />
                );
            case "Connection Request Send":
                return (
                    <ConnectionRequestSend
                        isMobile={isMobile}
                        onOpenConnectionRequest={() => setActiveItem("Connection Request")}
                    />
                );
            case "Global Members":
                return selectedMemberId ? (
                    <MembersProfile
                        memberID={selectedMemberId}
                        onBack={() => setSelectedMemberId(null)}
                    />
                ) : (
                    <GlobalMembers
                        isMobile={isMobile}
                        onOpenMemberProfile={(id) => setSelectedMemberId(id)}
                    />
                );
            case "My Listings":
                return <MyListings />;
            case "My Portfolio":
                return <MyPortfolio />;
            case "Global Portfolio":
                return <GlobalPortfolio />;
            default:
                return <MyConnection />;
        }
    };


    function fetchDetailsUser(userid) {
        // const { navigate } = this.props.navigation;
        let formdata = new FormData();
        formdata.append("user_id", userid)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        fetch(baseURL.URL + '/users/get_user_details', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server.result_code === 200) {
                    const result = responseJson.response
                    localStorage.setItem('connection_count', result.connection_count)
                    localStorage.setItem('listing_count', result.listing_count)
                    localStorage.setItem('portfolio_count', result.portfolio_count)
                    setAppLanguageAndNotify(result.app_language)

                    if (result.state == '' || result.state == '0' || result.state == ' ' || result.country == '' || result.country == ' ') {
                        navigate('InfoDetails')
                        // this.setState({ isProfileComplete: false })
                    }
                    else {
                        console.log("$$$$$$$$$$$$$$test")
                        // this.setState({ isProfileComplete: true })
                    }
                }
                else {

                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    function countListings(userid) {
        //https://yesmls.com/yes100/api/portfolio_feed/count_available_listings
        // const { navigate } = this.props.navigation;
        let formdata = new FormData();
        formdata.append("user_id", userid)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")

        fetch(baseURL.URL + '/portfolio_feed/count_available_listings', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                const result = responseJson
                console.log(result)
                if (responseJson.server.code === 200) {
                    const nextCount = Number(responseJson.response.listing_count || 0);
                    localStorage.setItem('available_listing_count', String(nextCount));
                    setAvailableListingCount(nextCount);
                    window.dispatchEvent(
                        new CustomEvent('availableListingCountUpdated', { detail: { count: nextCount } })
                    );
                }
                else {
                    //   Toast.showWithGravity(responseJson.server.message, Toast.SHORT, Toast.BOTTOM)
                }
            })
            .catch((error) => {
                console.error(error);
            });


    }

    return (
        <section className="home">
            <div className="container">
                <div className={`content-holder${isMobileHomeHub ? ' home-sidebar-only' : ''}`}>
                    {/* Show sidebar: always on desktop, on mobile when on /home or /member-profile */}
                    {(!isMobile || isHomeHubPath || location.pathname === '/member-profile' || location.pathname === '/home/global-members-details') && (
                        <MainSidebar
                            activeItem={activeItem}
                            setActiveItem={handleItemClick}
                        />
                    )}
                    {!isMobileHomeHub && (
                        <div
                            className={`content-right-wrapper${isMobileContentPage ? ' mobile-page-open' : ''}`}
                        >
                            {showDefaultMobilePageHeader && (
                                <div className="mobile-page-header">
                                    <button className="mobile-back-btn" onClick={handleBackClick}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M15 18L9 12L15 6"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>

                                    </button>
                                    <h2 className="mobile-page-title">
                                        {location.pathname === '/home/blocked-members'
                                            ? 'Blocked Members'
                                            : location.pathname === '/home/member-filter'
                                                ? 'Member Filter'
                                                : location.pathname === '/home/member-filter-my-connection'
                                                    ? 'My Connection Filter'
                                                    : location.pathname === '/home/my-connection-create-filter'
                                                        ? 'Create Filter'
                                                        : location.pathname === '/home/member-create-filter'
                                                            ? 'Create Filter'
                                                            : location.pathname === '/home/add-listing'
                                                                ? 'New listing input'
                                                                : location.pathname === '/home/edit-listing'
                                                                    ? 'Edit listing'
                                                                    : location.pathname === '/home/inactive-listing'
                                                                        ? 'Inactive Listings'
                                                                        : location.pathname === '/home/my-listing-contract'
                                                                            ? 'Terms & Conditions'
                                                                            : location.pathname === '/home/portfolio-filter'
                                                                                ? 'Portfolio Filter'
                                                                                : location.pathname === '/home/portfolio-favorite'
                                                                                    ? 'Portfolio Favorite'
                                                                                    : activeItem === 'My Connection'
                                                                                        ? 'My Connections'
                                                                                        : activeItem === 'Connection Request'
                                                                                            ? 'Connection Requests'
                                                                                            : activeItem}
                                    </h2>

                                </div>
                            )}
                            {location.pathname === '/member-profile' || location.pathname === '/home/global-members-details' ? (
                                <MembersProfile
                                    memberID={location.state?.memberID}
                                    member={location.state?.member}
                                    onBack={() => navigate('/home/global-members')}
                                />
                            ) : (
                                renderContent()
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

